import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check, Copy, ExternalLink, FileText, Globe, LayoutGrid, Link2,
  Mail, MapPin, Megaphone, Search, Settings, Share2, Sparkles, Star, Users, Plug,
} from 'lucide-react';
import AddOnPrompt from '../AddOnPrompt';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../../utils/apiClient';
import { useFacility } from '../../context/FacilityContext';
import './masSurface.css';

/**
 * The M.A.S marketing surface — rendered in M.A.S's OWN design language
 * (MAS-SURFACE-DESIGN.md v1) as the tenant-style app from the connection
 * template's STATE 3: a module nav per facility, each view backed by that
 * facility's own tenant key through the server-side passthrough.
 *
 * Isolation rule: a facility page renders ONLY its own tenant — the
 * portal's business picker is deliberately a static label here, never a
 * switcher, and no sibling facility's data is ever fetched.
 *
 * Keys never reach the browser: every engine call goes to
 * /api/mas/panel/engine/{path}, which attaches the decrypted key
 * server-side after checking the operator's facility access.
 */

const engine = (method, facilityId, path, body) =>
  apiClient.request({
    method,
    url: `/api/mas/panel/engine/${path}`,
    params: { facility_id: facilityId },
    ...(body !== undefined ? { data: body } : {}),
  });

/* Tolerant reader (§6): the engine returns bare arrays for some routes and
   named envelopes for others; unknown keys are ignored, never fatal. */
const arr = (data, key) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data[key])) return data[key];
  return Object.values(data).find(Array.isArray) || [];
};

const MODULES = [
  { key: 'dashboard', label: 'Dashboard', Icon: LayoutGrid },
  { key: 'website', label: 'Website', Icon: Globe },
  { key: 'keywords', label: 'Keywords', Icon: Search },
  { key: 'subpages', label: 'Subpages', Icon: FileText },
  { key: 'local', label: 'Local', Icon: MapPin },
  { key: 'competitors', label: 'Competitors', Icon: Users },
  { key: 'reviews', label: 'Reviews', Icon: Star },
  { key: 'social', label: 'Social', Icon: Share2 },
  { key: 'followups', label: 'Follow-ups', Icon: Mail },
  { key: 'setup', label: 'Setup', Icon: Settings },
];

/* ------------------------------------------------------------ shell */

const MasPanel = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?.id || currentFacility?._id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!facilityId) { setData(null); setLoading(false); return; }
    try {
      const res = await apiClient.get('/api/mas/panel', {
        params: { facility_id: facilityId },
      });
      setData(res.data);
    } catch {
      setData({ state: 'unreachable' });
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  if (!facilityId) {
    return (
      <div className="mas-surface">
        <p className="mas-loading">Select a facility to see its marketing.</p>
      </div>
    );
  }
  if (loading) {
    return <div className="mas-surface"><p className="mas-loading">Loading…</p></div>;
  }

  if (!data || data.state === 'unreachable') {
    return (
      <div className="mas-surface">
        <Card>
          <Empty
            Icon={Plug}
            title="The marketing engine is unreachable"
            text="Nothing is shown rather than stale numbers. This is a connection problem, not a data problem — your marketing keeps running engine-side."
            action={<button type="button" className="mas-btn"
              onClick={() => { setLoading(true); load(); }}>Try again</button>}
          />
        </Card>
      </div>
    );
  }

  if (data.state === 'connect' && data.error !== 'key_rejected') {
    return (
      <AddOnPrompt
        icon={Megaphone} name="Marketing" appKey="mas"
        summary="Grows this facility’s online presence and brings in renters, run for you."
        points={[
          'Website pages and local search kept current',
          'Reviews and social posts managed',
          'Follow-ups to leads who have not rented yet',
        ]}
      />
    );
  }

  if (data.state === 'connect') {
    return (
      <div className="mas-surface">
        <Card>
          <Empty
            Icon={Plug}
            title="The engine rejected this location’s key"
            text="Marketing stays paused until a current tenant key is in place. Replace it in Settings → Third-party apps."
            action={(
              <Link to="/settings?tab=apps&app=mas" className="mas-btn"
                style={{ textDecoration: 'none', display: 'inline-block' }}>
                Open third-party apps
              </Link>
            )}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="mas-surface">
      <TenantApp data={data} facilityId={facilityId} reload={load} />
    </div>
  );
};

const TenantApp = ({ data, facilityId, reload }) => {
  const [module, setModule] = useState(
    data.state === 'setup' ? 'setup' : 'dashboard');
  const openCount = (data.tasks || []).length;
  const name = data.tenant_name || data.facility_name || 'This location';
  const active = MODULES.find((m) => m.key === module) || MODULES[0];

  return (
    <div className="mas-app">
      <aside className="mas-side">
        <div className="mas-brand">
          <span className="mas-brand-badge">M</span>
          <span>
            {/* Provider-neutral (owner, 2026-08-29): the marketing engine is a
                microservice each facility may point elsewhere — no vendor
                name in the product. */}
            <span className="mas-brand-name">Marketing engine</span>
            <span className="mas-brand-sub" style={{ display: 'block' }}>connected per facility</span>
          </span>
        </div>
        {/* Static, not a switcher — isolation rule. */}
        <div className="mas-tenant" title={name}>{name}</div>
        <nav className="mas-navlist">
          {MODULES.map(({ key, label, Icon }) => (
            <button key={key} type="button"
              className={`mas-navrow${module === key ? ' is-active' : ''}`}
              onClick={() => setModule(key)}>
              <Icon size={17} />
              {label}
              {key === 'dashboard' && openCount > 0 && (
                <span className="mas-count mas-num">{openCount}</span>
              )}
            </button>
          ))}
        </nav>
      </aside>

      <main className="mas-main">
        <div className="mas-head">
          <div>
            <p className="mas-eyebrow">{name}</p>
            <h1 className="mas-title">{active.label}</h1>
          </div>
          <div className="mas-head-right">
            {data.state === 'setup' ? (
              <span className="mas-pill p-wait">Setup</span>
            ) : (
              <span className="mas-pill p-live">Connected</span>
            )}
          </div>
        </div>

        {data.state === 'setup' && module !== 'setup' && (
          <div className="mas-banner b-warn">
            Setup isn&rsquo;t finished, so these numbers may be incomplete —
            finish the checks in Setup to unlock the full picture.
          </div>
        )}

        {module === 'dashboard' && <Dashboard data={data} facilityId={facilityId} reload={reload} />}
        {module === 'website' && <WebsiteModule facilityId={facilityId} />}
        {module === 'keywords' && <KeywordsModule facilityId={facilityId} />}
        {module === 'subpages' && <SubpagesModule facilityId={facilityId} />}
        {module === 'local' && <LocalModule facilityId={facilityId} />}
        {module === 'competitors' && <CompetitorsModule facilityId={facilityId} />}
        {module === 'reviews' && <ReviewsModule facilityId={facilityId} />}
        {module === 'social' && <SocialModule facilityId={facilityId} />}
        {module === 'followups' && <FollowupsModule facilityId={facilityId} />}
        {module === 'setup' && <SetupModule facilityId={facilityId} onReady={reload} />}
      </main>
    </div>
  );
};

/* ------------------------------------------------------- primitives */

const Card = ({ title, hint, children, bodyless }) => (
  <section className="mas-card">
    {title && (
      <div className="mas-card-head">
        <span className="mas-card-title">{title}</span>
        {hint && <span className="mas-card-hint">{hint}</span>}
      </div>
    )}
    {bodyless ? children : <div className="mas-card-body">{children}</div>}
  </section>
);

const Empty = ({ Icon, title, text, action }) => (
  <div className="mas-empty">
    <span className="mas-empty-icon"><Icon size={20} /></span>
    <span className="mas-empty-title">{title}</span>
    <span className="mas-empty-text">{text}</span>
    {action}
  </div>
);

const Kpi = ({ label, value, sub, hero }) => (
  <div className={`mas-kpi${hero ? ' is-hero' : ''}`}>
    <p className="mas-kpi-label">{label}</p>
    <p className="mas-kpi-value mas-num">{value}</p>
    {sub && <p className="mas-kpi-sub">{sub}</p>}
  </div>
);

const Ring = ({ score }) => {
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  const color = n >= 80 ? 'var(--ring-good)' : n >= 50 ? 'var(--ring-warn)' : 'var(--ring-bad)';
  return (
    <div className="mas-ring"
      style={{ background: `conic-gradient(${color} ${n * 3.6}deg, var(--sunk) 0deg)` }}>
      <div className="mas-ring-inner mas-num">{n}</div>
    </div>
  );
};

const Busy = ({ label, busyLabel, onClick, busy, ghost, sm }) => (
  <button type="button" onClick={onClick} disabled={busy}
    className={`mas-btn${ghost ? ' is-ghost' : ''}${sm ? ' is-sm' : ''}`}>
    {busy ? busyLabel : label}
  </button>
);

const copy = (text) => {
  navigator.clipboard.writeText(text)
    .then(() => toast.success('Copied.'))
    .catch(() => toast.error('Could not copy.'));
};

/* Tolerant module fetch: distinguishes "engine doesn't serve this yet"
   from an outage, and never crashes on an unexpected shape. */
const useEngine = (facilityId, path) => {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const refetch = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await engine('get', facilityId, path);
      setState({ loading: false, data: r.data, error: null });
    } catch (e) {
      const code = e?.response?.status;
      setState({
        loading: false, data: null,
        error: code === 404 ? 'not_served' : code === 401 ? 'key_rejected' : 'unreachable',
      });
    }
  }, [facilityId, path]);
  useEffect(() => { refetch(); }, [refetch]);
  return { ...state, refetch };
};

const ModuleState = ({ loading, error, retry, children }) => {
  if (loading) return <p className="mas-loading">Loading…</p>;
  if (error === 'not_served') {
    return (
      <Card>
        <Empty Icon={Sparkles} title="This module isn't switched on yet"
          text="The engine doesn't serve this module for your plan yet. It appears here automatically the moment it does — nothing to install."
          action={<Busy label="Check again" onClick={retry} />} />
      </Card>
    );
  }
  if (error === 'key_rejected') {
    return (
      <Card>
        <Empty Icon={Plug} title="The engine rejected this location's key"
          text="Ask your marketing engine provider for a current tenant key for this location, then reconnect it. Nothing else is affected."
          action={<Busy label="Try again" onClick={retry} ghost />} />
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <Empty Icon={Plug} title="The marketing engine is unreachable"
          text="Nothing is shown rather than stale numbers. Your marketing keeps running engine-side."
          action={<Busy label="Try again" onClick={retry} />} />
      </Card>
    );
  }
  return children;
};

const useAction = (refetch) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      if (refetch) await refetch();
    } catch (e) {
      toast.error(e?.response?.status === 404
        ? 'The engine doesn’t serve that yet.'
        : 'The engine could not complete that.');
    } finally { setBusy(false); }
  };
  return [busy, run];
};

/* -------------------------------------------------------- Dashboard */

const TASK_CLASS = { assisted: 's-assisted', approval: 's-approval', onboarding: 's-onboarding' };

const Dashboard = ({ data, facilityId, reload }) => {
  const dg = data.digest || {};
  const tasks = useMemo(() => data.tasks || [], [data.tasks]);
  const did = dg.agents_did || {};
  const engineDid = Object.values(did).reduce((n, v) => n + (Number(v) || 0), 0);
  const bySource = Object.entries((dg.leads || {}).by_source || {});
  const rev = dg.reviews || {};
  const upcoming = dg.upcoming_posts || [];

  const groups = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => {
      const g = t.module || 'general';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(t);
    });
    return [...map.entries()];
  }, [tasks]);

  const act = async (taskId, status) => {
    try {
      await apiClient.patch(`/api/mas/panel/tasks/${taskId}`,
        { status, facility_id: facilityId });
      reload();
    } catch { toast.error('Could not update that task.'); }
  };

  return (
    <>
      <div className="mas-kpis">
        <Kpi hero label="Engine did" value={engineDid}
          sub={engineDid ? 'actions this week' : 'nothing yet this week'} />
        <Kpi label="Leads" value={(dg.leads || {}).total || 0}
          sub={bySource.length ? bySource.map(([k, v]) => `${v} ${k}`).join(' · ') : 'no sources yet'} />
        <Kpi label="Converted" value={(dg.conversions || {}).total || 0}
          sub={`$${((dg.conversions || {}).value_usd_month || 0).toFixed(0)}/mo value`} />
        <Kpi label="Rating" value={rev.avg_rating != null ? `${rev.avg_rating}★` : '—'}
          sub={rev.target ? `target ${rev.target}★` : 'no reviews yet'} />
      </div>

      <Card title="Needs you"
        hint={tasks.length
          ? `${tasks.length} open — everything else the engine handles`
          : 'everything else the engine handles'}>
        {tasks.length === 0 ? (
          <Empty Icon={Check} title="Nothing is waiting on you"
            text="The engine is running this location on its own. Work appears here only when a surface has no API and needs a human — you'll see it with the copy already written." />
        ) : groups.map(([group, rows]) => (
          <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {groups.length > 1 && (
              <p className="mas-eyebrow" style={{ marginTop: 2 }}>{group}</p>
            )}
            {rows.map((t) => (
              <div key={t.task_id} className="mas-item">
                <div className="mas-item-top">
                  {t.class && (
                    <span className={`mas-sev ${TASK_CLASS[t.class] || 's-low'}`}>{t.class}</span>
                  )}
                  <span className="mas-item-title">{t.title}</span>
                  <span className="mas-item-right">
                    {t.est_minutes ? <span className="mas-item-min mas-num">~{t.est_minutes} min</span> : null}
                  </span>
                </div>
                {t.body && <div className="mas-item-body">{t.body}</div>}
                <div className="mas-actions">
                  {t.paste_ready && t.body && (
                    <button type="button" className="mas-link" onClick={() => copy(t.body)}>
                      <Copy size={13} /> Copy text
                    </button>
                  )}
                  {t.open_url && (
                    <a className="mas-link" href={t.open_url} target="_blank" rel="noreferrer">
                      <ExternalLink size={13} /> Open
                    </a>
                  )}
                  {t.class === 'approval' ? (
                    <>
                      <button type="button" className="mas-link is-good"
                        onClick={() => act(t.task_id, 'approved')}>Approve</button>
                      <button type="button" className="mas-link is-danger"
                        onClick={() => act(t.task_id, 'rejected')}>Reject</button>
                    </>
                  ) : (
                    <button type="button" className="mas-link"
                      onClick={() => act(t.task_id, 'done')}>Mark done</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </Card>

      {engineDid > 0 && (
        <Card title="Engine activity" hint="no touches needed">
          <p className="mas-why">What the engine did for this location this week, on its own.</p>
          <div className="mas-chips">
            {Object.entries(did).map(([k, v]) => (
              <span key={k} className="mas-chip mas-num">{v} {k.replace(/_/g, ' ')}</span>
            ))}
          </div>
        </Card>
      )}

      {upcoming.length > 0 && (
        <Card title="Upcoming posts" hint={`${upcoming.length} scheduled`}>
          <p className="mas-why">Written and scheduled by the engine — edit or delete any before its date in Social.</p>
          {upcoming.map((p) => (
            <div key={p.post_id} className="mas-item">
              <div className="mas-item-top">
                <span className="mas-item-title">{p.content}</span>
                <span className="mas-item-right">
                  <span className="mas-chip mas-num">{p.scheduled_date}</span>
                </span>
              </div>
              {Array.isArray(p.platforms) && p.platforms.length > 0 && (
                <div className="mas-chips">
                  {p.platforms.map((pl) => <span key={pl} className="mas-chip">{pl}</span>)}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}
    </>
  );
};

/* ---------------------------------------------------------- Website */

const WebsiteModule = ({ facilityId }) => {
  const [result, setResult] = useState(null);
  const [busy, run] = useAction(null);
  const audit = () => run(async () => {
    const r = await engine('post', facilityId, 'optimise', {});
    setResult(r.data);
  });

  const findings = result ? arr(result.findings || result, 'findings') : [];
  const gated = result && result.error === 'no_url';
  const unreachable = result && result.error === 'unreachable';

  return (
    <>
      <Card title="Website audit"
        hint={result && result.url ? result.url : 'scored against what wins local search'}>
        <p className="mas-why">
          Your site is where every ad, listing and search result sends people —
          a slow or unclear page wastes all of it.
        </p>
        {!result ? (
          <Empty Icon={Globe} title="No audit yet"
            text="Run the audit to score this location's website and get the specific fixes, written out, in priority order."
            action={<Busy label="Run audit" busyLabel="Auditing…" onClick={audit} busy={busy} />} />
        ) : gated ? (
          <Empty Icon={Link2} title="Add a website to unlock the audit"
            text="Set this location's website URL in facility settings — the engine audits the live page, so it needs the address first."
            action={<Busy label="Run audit" busyLabel="Auditing…" onClick={audit} busy={busy} ghost />} />
        ) : unreachable ? (
          <Empty Icon={Globe} title="The site didn't respond"
            text={result.message || 'The engine could not load the page. Check the URL is public and try again.'}
            action={<Busy label="Try again" busyLabel="Auditing…" onClick={audit} busy={busy} />} />
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <Ring score={result.score} />
              <div>
                <p style={{ fontSize: 13, fontWeight: 650 }}>
                  {result.score >= 80 ? 'Strong' : result.score >= 50 ? 'Needs work' : 'Losing customers'}
                </p>
                <p className="mas-why">
                  {findings.length} finding{findings.length === 1 ? '' : 's'} — fixing the high ones first moves the most.
                </p>
                <div style={{ marginTop: 8 }}>
                  <Busy label="Re-run audit" busyLabel="Auditing…" onClick={audit} busy={busy} ghost sm />
                </div>
              </div>
            </div>
          </>
        )}
      </Card>

      {findings.length > 0 && (
        <Card title="What to fix" hint="highest impact first">
          {findings.map((f, i) => (
            <div key={f.key || i} className="mas-item">
              <div className="mas-item-top">
                {f.severity && (
                  <span className={`mas-sev s-${String(f.severity).toLowerCase()}`}>{f.severity}</span>
                )}
                <span className="mas-item-title">{f.finding || f.key}</span>
              </div>
              {f.why && <div className="mas-item-body">{f.why}</div>}
            </div>
          ))}
        </Card>
      )}
    </>
  );
};

/* --------------------------------------------------------- Keywords */

const KeywordsModule = ({ facilityId }) => {
  const { loading, data, error, refetch } = useEngine(facilityId, 'keywords');
  const [busy, run] = useAction(refetch);
  const keywords = arr(data, 'keywords');
  const derive = () => run(() => engine('post', facilityId, 'keywords/derive', {}),
    'Keywords derived.');

  return (
    <ModuleState loading={loading} error={error} retry={refetch}>
      <Card title="Keywords" hint={keywords.length ? `${keywords.length} terms` : null}>
        <p className="mas-why">
          The exact phrases people type when they need what you rent — every
          page, post and listing is written to match them.
        </p>
        {keywords.length === 0 ? (
          <Empty Icon={Search} title="No keywords yet"
            text="The engine reads this location's profile and derives the terms real customers search — then writes to them."
            action={<Busy label="Derive keywords" busyLabel="Deriving…" onClick={derive} busy={busy} />} />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Busy label="Re-derive" busyLabel="Deriving…" onClick={derive} busy={busy} ghost sm />
            </div>
            {keywords.map((k, i) => (
              <div key={k.term || i} className="mas-item">
                <div className="mas-item-top">
                  <span className="mas-item-title">{k.term}</span>
                  <span className="mas-item-right">
                    {k.intent && <span className="mas-chip">{k.intent}</span>}
                  </span>
                </div>
                {k.why && <div className="mas-item-body">{k.why}</div>}
              </div>
            ))}
          </>
        )}
      </Card>
    </ModuleState>
  );
};

/* --------------------------------------------------------- Subpages */

const SubpagesModule = ({ facilityId }) => {
  const { loading, data, error, refetch } = useEngine(facilityId, 'keywords');
  const [busy, run] = useAction(refetch);
  const pages = (data && Array.isArray(data.pages)) ? data.pages : [];
  const derive = () => run(() => engine('post', facilityId, 'keywords/derive', {}),
    'Pages planned.');

  return (
    <ModuleState loading={loading} error={error} retry={refetch}>
      <Card title="Subpages" hint={pages.length ? `${pages.length} planned` : null}>
        <p className="mas-why">
          One page per thing people search for. A page that answers the exact
          search outranks a single page trying to answer everything.
        </p>
        {pages.length === 0 ? (
          <Empty Icon={FileText} title="No pages planned yet"
            text="The engine groups your keywords into pages worth building, each with the terms it should own."
            action={<Busy label="Plan pages" busyLabel="Planning…" onClick={derive} busy={busy} />} />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Busy label="Re-plan" busyLabel="Planning…" onClick={derive} busy={busy} ghost sm />
            </div>
            {pages.map((p, i) => (
              <div key={p.slug || i} className="mas-item">
                <div className="mas-item-top">
                  <span className="mas-item-title">/{p.slug}</span>
                  <span className="mas-item-right">
                    {p.intent && <span className="mas-chip">{p.intent}</span>}
                  </span>
                </div>
                {Array.isArray(p.terms) && p.terms.length > 0 && (
                  <div className="mas-chips">
                    {p.terms.map((t) => <span key={t} className="mas-chip">{t}</span>)}
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </Card>
    </ModuleState>
  );
};

/* ------------------------------------------------------------ Local */

const LISTING_PILL = {
  verified: 'p-live', listed: 'p-live', unknown: 'p-wait', not_applicable: 'p-off',
};

const LocalModule = ({ facilityId }) => {
  const { loading, data, error, refetch } = useEngine(facilityId, 'listings');
  const [busy, run] = useAction(refetch);
  const listings = arr(data, 'listings');
  const nap = (data && data.nap) || null;
  const napText = nap ? [nap.name, nap.address, nap.phone, nap.website]
    .filter(Boolean).join('\n') : '';
  const claimed = listings.filter((l) => l.status === 'listed' || l.status === 'verified').length;

  const derive = () => run(() => engine('post', facilityId, 'listings/derive', {}),
    'Directories refreshed.');
  const mark = (dir, status) => run(
    () => engine('patch', facilityId, `listings/${dir}`, { status }), 'Updated.');

  return (
    <ModuleState loading={loading} error={error} retry={refetch}>
      {nap && (
        <Card title="Your exact details" hint="use this block everywhere">
          <p className="mas-why">
            Local ranking reads consistency — a stray suite number or a
            different phone format on one directory costs you position.
          </p>
          <div className="mas-nap">{napText}</div>
          <div className="mas-actions">
            <button type="button" className="mas-link" onClick={() => copy(napText)}>
              <Copy size={13} /> Copy details
            </button>
          </div>
        </Card>
      )}

      <Card title="Directories"
        hint={listings.length ? `${claimed}/${listings.length} listed` : null}>
        <p className="mas-why">
          Each of these feeds the map results people use to pick a facility.
        </p>
        {listings.length === 0 ? (
          <Empty Icon={MapPin} title="No directories mapped yet"
            text="The engine picks the directories that matter for this kind of business and tracks which ones you hold."
            action={<Busy label="Find directories" busyLabel="Finding…" onClick={derive} busy={busy} />} />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Busy label="Refresh" busyLabel="Refreshing…" onClick={derive} busy={busy} ghost sm />
            </div>
            {listings.map((l) => (
              <div key={l.directory} className="mas-item">
                <div className="mas-item-top">
                  <span className="mas-item-title">{l.name || l.directory}</span>
                  <span className="mas-item-right">
                    <span className={`mas-pill ${LISTING_PILL[l.status] || 'p-off'}`}>
                      {String(l.status || 'unknown').replace(/_/g, ' ')}
                    </span>
                  </span>
                </div>
                {l.why && <div className="mas-item-body">{l.why}</div>}
                <div className="mas-actions">
                  {(l.listing_url || l.signup_url) && (
                    <a className="mas-link" href={l.listing_url || l.signup_url}
                      target="_blank" rel="noreferrer">
                      <ExternalLink size={13} /> {l.listing_url ? 'View listing' : 'Claim listing'}
                    </a>
                  )}
                  {l.status !== 'listed' && l.status !== 'verified' && (
                    <button type="button" className="mas-link is-good" disabled={busy}
                      onClick={() => mark(l.directory, 'listed')}>
                      Mark as listed
                    </button>
                  )}
                  {l.status !== 'not_applicable' && (
                    <button type="button" className="mas-link" disabled={busy}
                      onClick={() => mark(l.directory, 'not_applicable')}>
                      Not applicable
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </Card>
    </ModuleState>
  );
};

/* ------------------------------------------------------ Competitors */

const CompetitorsModule = ({ facilityId }) => {
  const { loading, data, error, refetch } = useEngine(facilityId, 'competitors');
  const [busy, run] = useAction(refetch);
  const rows = arr(data, 'competitors');
  const scan = () => run(() => engine('post', facilityId, 'competitors/scan', {}),
    'Scan complete.');

  return (
    <ModuleState loading={loading} error={error} retry={refetch}>
      <Card title="Competitors" hint={rows.length ? `${rows.length} nearby` : null}>
        <p className="mas-why">
          Who else shows up when your customers search — and what they do that
          you don&rsquo;t.
        </p>
        {rows.length === 0 ? (
          <Empty Icon={Users} title="No competitors mapped yet"
            text="The engine finds the businesses competing for the same searches and reports what they do differently."
            action={<Busy label="Scan competitors" busyLabel="Scanning…" onClick={scan} busy={busy} />} />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Busy label="Re-scan" busyLabel="Scanning…" onClick={scan} busy={busy} ghost sm />
            </div>
            {rows.map((c, i) => (
              <div key={c.name || i} className="mas-item">
                <div className="mas-item-top">
                  <span className="mas-item-title">{c.name}</span>
                  <span className="mas-item-right">
                    {c.rating != null && <span className="mas-chip mas-num">{c.rating}★</span>}
                  </span>
                </div>
                {c.why && <div className="mas-item-body">{c.why}</div>}
                {c.website && (
                  <div className="mas-actions">
                    <a className="mas-link" href={c.website} target="_blank" rel="noreferrer">
                      <ExternalLink size={13} /> Visit site
                    </a>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </Card>
    </ModuleState>
  );
};

/* ---------------------------------------------------------- Reviews */

const REPLY_PILL = {
  posted: 'p-live', approved: 'p-live', drafted: 'p-wait',
  assisted: 'p-wait', none: 'p-off',
};

const ReviewsModule = ({ facilityId }) => {
  const { loading, data, error, refetch } = useEngine(facilityId, 'reviews');
  const bench = useEngine(facilityId, 'reviews/benchmark');
  const [busy, run] = useAction(() => Promise.all([refetch(), bench.refetch()]));
  const rows = arr(data, 'reviews');
  const b = bench.data || {};

  return (
    <ModuleState loading={loading} error={error} retry={refetch}>
      <div className="mas-kpis">
        <Kpi label="Rating" value={b.avg_rating != null ? `${b.avg_rating}★` : '—'}
          sub={b.target ? `target ${b.target}★` : 'no target yet'} />
        <Kpi label="Reviews" value={b.review_count || 0}
          sub={b.new_this_period ? `${b.new_this_period} new this month` : 'none this month'} />
        <Kpi label="Gap to target" value={b.gap_to_target != null ? `${b.gap_to_target}★` : '—'}
          sub="how far from where you rank well" />
        <Kpi label="Awaiting reply" value={b.unreplied || 0}
          sub="replies lift ranking and trust" />
      </div>

      <Card title="Reviews" hint="the strongest lever on map ranking">
        <p className="mas-why">
          Rating and reply rate both feed local ranking — the engine drafts every
          reply, you approve.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Busy label="Fetch new reviews" busyLabel="Fetching…" busy={busy} ghost sm
            onClick={() => run(() => engine('post', facilityId, 'reviews/ingest', {}), 'Reviews fetched.')} />
          <Busy label="Draft replies" busyLabel="Drafting…" busy={busy} ghost sm
            onClick={() => run(() => engine('post', facilityId, 'reviews/draft', {}), 'Replies drafted.')} />
        </div>
        {rows.length === 0 ? (
          <Empty Icon={Star} title="No reviews pulled in yet"
            text="Fetch reviews to see what customers say and let the engine draft each reply for your approval."
            action={<Busy label="Fetch new reviews" busyLabel="Fetching…" busy={busy}
              onClick={() => run(() => engine('post', facilityId, 'reviews/ingest', {}), 'Reviews fetched.')} />} />
        ) : rows.map((r, i) => (
          <div key={r.review_id || i} className="mas-item">
            <div className="mas-item-top">
              <span className="mas-item-title">
                {r.author || 'Anonymous'}
                {r.rating != null && <span className="mas-num"> · {r.rating}★</span>}
              </span>
              <span className="mas-item-right">
                {r.platform && <span className="mas-chip">{r.platform}</span>}
                <span className={`mas-pill ${REPLY_PILL[r.reply_status] || 'p-off'}`}>
                  {String(r.reply_status || 'none').replace(/_/g, ' ')}
                </span>
              </span>
            </div>
            {r.text && <div className="mas-item-body">{r.text}</div>}
            {r.reply_text && (
              <div className="mas-item-body" style={{ color: 'var(--ink)' }}>
                <b>Drafted reply:</b> {r.reply_text}
              </div>
            )}
            <div className="mas-actions">
              {r.reply_text && (
                <button type="button" className="mas-link" onClick={() => copy(r.reply_text)}>
                  <Copy size={13} /> Copy reply
                </button>
              )}
              {r.reply_status === 'drafted' && (
                <button type="button" className="mas-link is-good" disabled={busy}
                  onClick={() => run(() => engine('patch', facilityId, `reviews/${r.review_id}`,
                    { reply_status: 'approved' }), 'Reply approved.')}>
                  Approve reply
                </button>
              )}
            </div>
          </div>
        ))}
      </Card>
    </ModuleState>
  );
};

/* ----------------------------------------------------------- Social */

const SocialModule = ({ facilityId }) => {
  const channels = useEngine(facilityId, 'channels');
  const posts = useEngine(facilityId, 'posts');
  const [busy, run] = useAction(() => Promise.all([channels.refetch(), posts.refetch()]));
  const chRows = arr(channels.data, 'channels');
  const postRows = arr(posts.data, 'posts');

  if (channels.loading || posts.loading) return <p className="mas-loading">Loading…</p>;
  if (channels.error && posts.error) {
    return (
      <ModuleState loading={false} error={channels.error}
        retry={() => { channels.refetch(); posts.refetch(); }} />
    );
  }

  return (
    <>
      <Card title="Channels" hint={chRows.length ? `${chRows.length} connected` : null}>
        <p className="mas-why">
          Where the engine posts. A channel that fails a check silently stops
          publishing — that&rsquo;s why the status is shown here.
        </p>
        {chRows.length === 0 ? (
          <Empty Icon={Share2} title="No channels connected"
            text="Your marketing engine provider connects the accounts the engine posts to. Once one is connected it appears here with its health."
            action={null} />
        ) : chRows.map((c) => (
          <div key={c.platform} className="mas-item">
            <div className="mas-item-top">
              <span className="mas-item-title">{c.platform}</span>
              <span className="mas-item-right">
                <span className={`mas-pill ${c.last_check_status === 'ok' ? 'p-live'
                  : c.last_check_status ? 'p-bad' : 'p-off'}`}>
                  {c.last_check_status || 'not checked'}
                </span>
              </span>
            </div>
            {c.last_check_message && <div className="mas-item-body">{c.last_check_message}</div>}
            <div className="mas-actions">
              <button type="button" className="mas-link" disabled={busy}
                onClick={() => run(() => engine('post', facilityId,
                  `channels/${c.platform}/check`, {}), 'Channel checked.')}>
                Check connection
              </button>
            </div>
          </div>
        ))}
      </Card>

      <Card title="Posts" hint={postRows.length ? `${postRows.length} in the queue` : null}>
        <p className="mas-why">
          Written by the engine and scheduled ahead — an empty queue means
          nothing publishes, so it tops itself up.
        </p>
        {postRows.length === 0 ? (
          <Empty Icon={Sparkles} title="Nothing queued yet"
            text="Generate a batch and the engine writes posts for this location, scheduled across the coming weeks."
            action={<Busy label="Generate posts" busyLabel="Writing…" busy={busy}
              onClick={() => run(() => engine('post', facilityId, 'posts/generate', {}), 'Posts generated.')} />} />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Busy label="Generate more" busyLabel="Writing…" busy={busy} ghost sm
                onClick={() => run(() => engine('post', facilityId, 'posts/generate', {}), 'Posts generated.')} />
            </div>
            {postRows.map((p) => (
              <div key={p.post_id} className="mas-item">
                <div className="mas-item-top">
                  <span className="mas-item-title">{p.content}</span>
                  <span className="mas-item-right">
                    {p.scheduled_date && <span className="mas-chip mas-num">{p.scheduled_date}</span>}
                    <span className={`mas-pill ${p.status === 'posted' ? 'p-live'
                      : p.status === 'failed' ? 'p-bad' : 'p-wait'}`}>
                      {String(p.status || '').replace(/_/g, ' ')}
                    </span>
                  </span>
                </div>
                {Array.isArray(p.platforms) && p.platforms.length > 0 && (
                  <div className="mas-chips">
                    {p.platforms.map((pl) => <span key={pl} className="mas-chip">{pl}</span>)}
                  </div>
                )}
                <div className="mas-actions">
                  <button type="button" className="mas-link is-danger" disabled={busy}
                    onClick={() => run(() => engine('delete', facilityId, `posts/${p.post_id}`), 'Post deleted.')}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </Card>
    </>
  );
};

/* -------------------------------------------------------- Follow-ups */

const FollowupsModule = ({ facilityId }) => {
  const { loading, data, error, refetch } = useEngine(facilityId, 'nurture/sequences');
  const [busy, run] = useAction(refetch);
  const rows = arr(data, 'sequences');

  return (
    <ModuleState loading={loading} error={error} retry={refetch}>
      <Card title="Follow-up sequences" hint={rows.length ? `${rows.length} sequences` : null}>
        <p className="mas-why">
          Most enquiries go cold because nobody followed up. These run
          automatically — and every message still obeys consent and quiet hours.
        </p>
        {rows.length === 0 ? (
          <Empty Icon={Mail} title="No sequences yet"
            text="The engine writes follow-up sequences for this location — new enquiries, move-ins and win-backs — for you to approve before any of them send."
            action={<Busy label="Write sequences" busyLabel="Writing…" busy={busy}
              onClick={() => run(() => engine('post', facilityId, 'nurture/derive', {}), 'Sequences written.')} />} />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Busy label="Re-write" busyLabel="Writing…" busy={busy} ghost sm
                onClick={() => run(() => engine('post', facilityId, 'nurture/derive', {}), 'Sequences written.')} />
            </div>
            {rows.map((s, i) => (
              <div key={s.sequence_id || i} className="mas-item">
                <div className="mas-item-top">
                  <span className="mas-item-title">{s.name}</span>
                  <span className="mas-item-right">
                    {s.trigger && <span className="mas-chip">{s.trigger}</span>}
                    <span className={`mas-pill ${s.status === 'live' ? 'p-live'
                      : s.status === 'paused' ? 'p-off' : 'p-wait'}`}>
                      {String(s.status || '').replace(/_/g, ' ')}
                    </span>
                  </span>
                </div>
                {Array.isArray(s.steps) && (
                  <div className="mas-item-body">
                    {s.steps.length} message{s.steps.length === 1 ? '' : 's'}
                    {s.steps[0]?.delay_hours != null && ` · first after ${s.steps[0].delay_hours}h`}
                  </div>
                )}
                {s.note && <div className="mas-item-body">{s.note}</div>}
                <div className="mas-actions">
                  {s.status !== 'live' && (
                    <button type="button" className="mas-link is-good" disabled={busy}
                      onClick={() => run(() => engine('patch', facilityId,
                        `nurture/sequences/${s.sequence_id}`, { status: 'live' }), 'Sequence live.')}>
                      Approve and start
                    </button>
                  )}
                  {s.status === 'live' && (
                    <button type="button" className="mas-link" disabled={busy}
                      onClick={() => run(() => engine('patch', facilityId,
                        `nurture/sequences/${s.sequence_id}`, { status: 'paused' }), 'Sequence paused.')}>
                      Pause
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </Card>
    </ModuleState>
  );
};

/* ------------------------------------------------------------ Setup */

const SetupModule = ({ facilityId, onReady }) => {
  const reqs = useEngine(facilityId, 'setup/requirements');
  const [checks, setChecks] = useState(null);
  const [busy, run] = useAction(reqs.refetch);
  const rows = checks || arr(reqs.data, 'requirements');

  const verify = () => run(async () => {
    const r = await engine('post', facilityId, 'setup/verify', {});
    setChecks(r.data?.checks || []);
    if (r.data?.ready && onReady) onReady();
  }, 'Checks updated.');

  return (
    <ModuleState loading={reqs.loading} error={reqs.error} retry={reqs.refetch}>
      <Card title="Setup" hint="what the engine needs to market this location">
        <p className="mas-why">
          The engine only markets what it can verify — each item below is
          something it needs from your data before it acts on your behalf.
        </p>
        {rows.length === 0 ? (
          <Empty Icon={Check} title="Nothing outstanding"
            text="The engine has everything it needs for this location. Run the checks any time you change addresses, hours or contact details."
            action={<Busy label="Run checks" busyLabel="Checking…" onClick={verify} busy={busy} />} />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Busy label="Run checks" busyLabel="Checking…" onClick={verify} busy={busy} ghost sm />
            </div>
            {rows.map((r, i) => {
              const ok = r.status === 'ok' || r.status === 'pass';
              return (
                <div key={r.key || i} className="mas-item">
                  <div className="mas-item-top">
                    <span className={`mas-pill ${ok ? 'p-live' : r.required === false ? 'p-off' : 'p-wait'}`}>
                      {ok ? 'ready' : r.required === false ? 'optional' : 'needed'}
                    </span>
                    <span className="mas-item-title">{r.key}</span>
                  </div>
                  {(r.detail || r.needed_for) && (
                    <div className="mas-item-body">{r.detail || r.needed_for}</div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </Card>
    </ModuleState>
  );
};

export default MasPanel;
